import { useState } from "react";
import { useAxios } from "../../../utils/useAxios";
import { TENANT_DOMAIN } from "../../../utils/domain";
import { ERROR_MESSAGES } from "../../../constant/constant";

/**
 * @typedef {Object} UserData
 * @property {string} f_name - First name of the school admin
 * @property {string} [l_name] - Last name of the school admin (optional)
 * @property {string} email - Email of the school admin
 * @property {string} role_slug - Role slug, e.g., "school-admin"
 * @property {string} password - Password for the school admin
 */

/**
 * @typedef {Object} SubSchoolData
 * @property {string} name - School name
 * @property {string} org_id - Organization ID
 * @property {string} email - School email
 * @property {string} subdomain - Subdomain for the school
 * @property {string} [phone] - Phone number (optional)
 * @property {string} [address] - Address (optional)
 * @property {File} [image] - School image file (optional)
 * @property {UserData} user - School admin user details
 */

/**
 * @typedef {Object} CreateSubSchoolResponse
 * @property {boolean} success - Whether the request was successful
 * @property {string} message - Response message
 * @property {Object} [data] - Created sub-school data
 */

/**
 * Custom hook to create a new sub-school under an organization
 */
export const useCreateSubSchool = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const { axiosInstance } = useAxios();

  /**
   * Creates a new sub-school under an organization
   * @param {SubSchoolData} schoolData - The sub-school data to create
   * @param {(message: string) => void} [onSuccess] - Callback function to be called on success
   * @returns {Promise<boolean>} - Returns true on success, false on failure
   */
  const createSubSchool = async (schoolData, onSuccess) => {
    setLoading(true);
    setError("");
    setData(null);

    try {
      const formData = new FormData();
      formData.append("name", schoolData.name);
      formData.append("email", schoolData.email);
      formData.append("subdomain", schoolData.subdomain);

      // Append user fields
      formData.append("user[f_name]", schoolData.user.f_name);
      formData.append("user[email]", schoolData.user.email);
      formData.append("user[role_slug]", schoolData.user.role_slug);

      // Add optional fields
      if (schoolData.phone) {
        formData.append("phone", schoolData.phone);
      }
      if (schoolData.address) {
        formData.append("address", schoolData.address);
      }
      if (schoolData.image) {
        formData.append("image", schoolData.image);
      }

      const response = await axiosInstance.post(
        `${TENANT_DOMAIN}/organization/admin/sub-school/store`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      setData(response.data);

      if (onSuccess) {
        onSuccess(response.data?.data?.institution?.id);
      }

      return true;
    } catch (err) {
      let errorMessage = "";

      if (err?.response?.data?.errors) {
        const errorKey = Object.keys(err.response.data.errors)[0];
        const firstErrorMessage = err.response.data.errors[errorKey][0];

        // Map API error keys to user-friendly messages
        const errorKeyMap = {
          "user.email": "Admin Email is already taken",
          email: "School Email is already taken",
          subdomain: "Subdomain is already taken",
          phone: "Phone number is already taken",
        };

        errorMessage = errorKeyMap[errorKey] || firstErrorMessage;
      } else if (err?.response?.status === 500) {
        errorMessage = ERROR_MESSAGES.INTERNAL_SERVER;
      } else {
        errorMessage =
          err?.response?.data?.message ||
          err?.message ||
          ERROR_MESSAGES.UNKNOWN;
      }

      setError(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    createSubSchool,
    loading,
    error,
    data,
    setError,
  };
};
